// Embed "this image was made by AI" provenance into a generated file, in the
// bytes themselves — so the marking travels with the file when it's downloaded
// to the device or saved into the Asset Lab (independent of any UI tag).
//
// We write the minimum required for compliance with Google's AI-content policy
// (Merchant Center et al.), which reads the IPTC "Digital Source Type" from the
// embedded XMP packet:
//   https://support.google.com/merchants/answer/14743464
//   https://iptc.org/news/google-announces-use-of-iptc-metadata-for-generative-ai-images/
//
// Two fields go into one XMP packet:
//   • Iptc4xmpExt:DigitalSourceType — the standard signal that the image is
//     AI-generated (trainedAlgorithmicMedia) or an AI-assisted edit
//     (compositeWithTrainedAlgorithmicMedia, for the on-device tools).
//   • xmp:CreatorTool / tiff:Software — the creating tool and engine, as one
//     string: "Asset Lab Magic Studio (<model or local tool>)" (the original ask).
//
// XMP is the carrier (not EXIF) because DigitalSourceType is an XMP-only field,
// and one packet works for both formats we emit: PNG (an iTXt chunk) and JPEG
// (an APP1 segment). Anything else is returned untouched.

// ─── IPTC Digital Source Type controlled-vocabulary values ───────────────────
// The full IRIs Google/IPTC expect as the field value (not the bare term).
export const DIGITAL_SOURCE_TYPE = {
  // Created with a generative model — a plain Magic Studio generation.
  trainedAlgorithmicMedia: 'http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia',
  // An existing image edited with a trained algorithm — our on-device tools
  // (background removal / upscale) operate on an input image.
  compositeWithTrainedAlgorithmicMedia:
    'http://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia',
} as const;

export type AiImageMeta = {
  // Human-readable generator → written to xmp:CreatorTool and tiff:Software.
  software: string;
  // One of the DIGITAL_SOURCE_TYPE IRIs above.
  sourceType: string;
};

// Pick the right Digital Source Type for a result: a cloud generation is
// trainedAlgorithmicMedia; an on-device tool edits an input, so it's a composite.
export function aiSourceType(source?: 'removeBg' | 'upscale'): string {
  return source
    ? DIGITAL_SOURCE_TYPE.compositeWithTrainedAlgorithmicMedia
    : DIGITAL_SOURCE_TYPE.trainedAlgorithmicMedia;
}

// ─── XMP packet ──────────────────────────────────────────────────────────────
function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// The XMP packet wrapper requires a BOM in its xpacket begin marker.
const BOM = String.fromCharCode(0xfeff);

// A minimal, compact (property-attribute form) XMP packet carrying the two
// fields. The leading BOM in xpacket begin is part of the XMP spec.
function buildXmpPacket(meta: AiImageMeta): string {
  const sw = xmlEscape(meta.software);
  const dst = xmlEscape(meta.sourceType);
  return (
    `<?xpacket begin="${BOM}" id="W5M0MpCehiHzreSzNTczkc9d"?>` +
    '<x:xmpmeta xmlns:x="adobe:ns:meta/">' +
    '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">' +
    '<rdf:Description rdf:about=""' +
    ' xmlns:Iptc4xmpExt="http://iptc.org/std/Iptc4xmpExt/2008-02-29/"' +
    ' xmlns:xmp="http://ns.adobe.com/xap/1.0/"' +
    ' xmlns:tiff="http://ns.adobe.com/tiff/1.0/"' +
    ` Iptc4xmpExt:DigitalSourceType="${dst}"` +
    ` xmp:CreatorTool="${sw}"` +
    ` tiff:Software="${sw}"/>` +
    '</rdf:RDF>' +
    '</x:xmpmeta>' +
    '<?xpacket end="w"?>'
  );
}

// ─── byte helpers ────────────────────────────────────────────────────────────
const utf8 = new TextEncoder();

// Latin-1 bytes — for PNG keyword / the JPEG XMP namespace signature, which are
// pure ASCII; avoids pulling UTF-8 multi-byte surprises into those headers.
function latin1(str: string): Uint8Array {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) out[i] = str.charCodeAt(i) & 0xff;
  return out;
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of parts) {
    out.set(p, pos);
    pos += p.length;
  }
  return out;
}

function u32be(value: number): Uint8Array {
  return new Uint8Array([
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff,
  ]);
}

// CRC-32 (IEEE 802.3) over a byte range — required for the PNG chunk we add.
// Computed table-free; only ever run over our small iTXt chunk, never the image.
function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i];
    for (let k = 0; k < 8; k++) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

// ─── PNG: insert an iTXt chunk carrying the XMP packet ───────────────────────
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const XMP_PNG_KEYWORD = 'XML:com.adobe.xmp';

function isPng(bytes: Uint8Array): boolean {
  return PNG_SIGNATURE.every((b, i) => bytes[i] === b);
}

function injectXmpIntoPng(bytes: Uint8Array, xmp: string): Uint8Array {
  // iTXt data layout: keyword \0 compressionFlag compressionMethod
  //   languageTag \0 translatedKeyword \0 text(UTF-8)
  // (uncompressed, no language / translated keyword).
  const data = concatBytes([
    latin1(XMP_PNG_KEYWORD),
    new Uint8Array([0x00, 0x00, 0x00, 0x00, 0x00]),
    utf8.encode(xmp),
  ]);
  const type = latin1('iTXt');
  const chunk = concatBytes([
    u32be(data.length),
    type,
    data,
    u32be(crc32(concatBytes([type, data]))),
  ]);

  // Insert right after IHDR (always the first chunk: 8-byte signature + a
  // length(4)+type(4)+data+crc(4) chunk). Read its length rather than assume 13.
  const ihdrDataLen = (bytes[8] << 24) | (bytes[9] << 16) | (bytes[10] << 8) | bytes[11];
  const insertAt = 8 + 12 + ihdrDataLen; // signature + (len+type+crc=12) + data
  return concatBytes([bytes.subarray(0, insertAt), chunk, bytes.subarray(insertAt)]);
}

// ─── JPEG: insert an APP1 segment carrying the XMP packet ────────────────────
const XMP_JPEG_NS = 'http://ns.adobe.com/xap/1.0/\0';

function isJpeg(bytes: Uint8Array): boolean {
  return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
}

function injectXmpIntoJpeg(bytes: Uint8Array, xmp: string): Uint8Array {
  const payload = concatBytes([latin1(XMP_JPEG_NS), utf8.encode(xmp)]);
  const segLength = payload.length + 2; // +2 for the length field itself
  if (segLength > 0xffff) return bytes; // too large for a single APP1 — skip
  const segment = concatBytes([
    new Uint8Array([0xff, 0xe1, (segLength >> 8) & 0xff, segLength & 0xff]),
    payload,
  ]);

  // Place after SOI, and after a leading JFIF APP0 if present (APP0 must stay
  // immediately after SOI), so the file remains spec-clean.
  let insertAt = 2; // past SOI (FFD8)
  if (bytes[2] === 0xff && bytes[3] === 0xe0) {
    const app0Len = (bytes[4] << 8) | bytes[5];
    insertAt = 4 + app0Len;
  }
  return concatBytes([bytes.subarray(0, insertAt), segment, bytes.subarray(insertAt)]);
}

// ─── public API ──────────────────────────────────────────────────────────────
// Return a new Blob with the AI-provenance XMP embedded. PNG and JPEG are
// supported (the only formats Magic Studio emits); any other input is returned
// unchanged. The format is detected from the bytes, not the Blob type / name,
// so it's correct even when the caller mislabels it.
export async function embedAiMetadata(blob: Blob, meta: AiImageMeta): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const xmp = buildXmpPacket(meta);

  if (isPng(bytes)) {
    return new Blob([injectXmpIntoPng(bytes, xmp)], { type: 'image/png' });
  }
  if (isJpeg(bytes)) {
    return new Blob([injectXmpIntoJpeg(bytes, xmp)], { type: 'image/jpeg' });
  }
  return blob;
}
